import axios from "axios";

/**
 * In production (Cloudflare Pages) we ALWAYS use same-origin "/api"
 * so the request is proxied by Pages Functions to your backend.
 * In local dev, fall back to env (e.g. http://127.0.0.1:8000).
 */
const isBrowser = typeof window !== "undefined";
const isProd = process.env.NODE_ENV === "production";

// allow overriding via env
const ENV_BASE =
  process.env.REACT_APP_API_BASE_URL || process.env.NEXT_PUBLIC_API_BASE_URL;

// Decide baseURL
const BASE_URL = isProd ? "/api" : ENV_BASE || "/api";

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  withCredentials: true,
});

// ---- Token utils ----
function getTokenWithExpiry() {
  if (!isBrowser) return null;
  const tokenStr = localStorage.getItem("accessToken");
  if (!tokenStr) return null;
  try {
    const { token, expiry } = JSON.parse(tokenStr);
    if (Date.now() > expiry) {
      localStorage.removeItem("accessToken");
      return null;
    }
    return token;
  } catch {
    localStorage.removeItem("accessToken");
    return null;
  }
}

function setAccessToken(token, expiresInSeconds = 3600) {
  if (!isBrowser) return;
  const expiry = Date.now() + expiresInSeconds * 1000;
  localStorage.setItem("accessToken", JSON.stringify({ token, expiry }));
}

// === Request Interceptor ===
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getTokenWithExpiry();
    if (token && !config.skipAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers["X-Requested-With"] = "XMLHttpRequest";

    if (
      !config.headers["Content-Type"] &&
      config.method &&
      config.method.toLowerCase() !== "get"
    ) {
      config.headers["Content-Type"] = "application/json";
    }

    // Prevent absolute http:// calls in prod (normalize to /api/*)
    if (isProd && config.url && /^https?:\/\//i.test(config.url)) {
      try {
        const u = new URL(config.url);
        config.url = `/api${u.pathname}${u.search}`;
      } catch {
        // ignore parse errors
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// === Response Interceptor (refresh if needed) ===
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.skipAuth
    ) {
      originalRequest._retry = true;
      try {
        // dùng relative path, không ép slash cuối
        const res = await axios.post(
          "/api/auth/token/refresh",
          {},
          { withCredentials: true }
        );
        const newAccessToken = res.data?.access_token;
        if (newAccessToken) {
          setAccessToken(newAccessToken);
          originalRequest.headers = {
            ...(originalRequest.headers || {}),
            Authorization: `Bearer ${newAccessToken}`,
          };
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        if (isBrowser) localStorage.removeItem("accessToken");
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
